import Papa from "papaparse";
import * as XLSX from "xlsx";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const HEADER_ALIASES = {
  employee_code: ["employee_code", "emp_code", "code", "employee id", "employee_id", "employeeid"],
  work_date: ["work_date", "attendance_date", "date", "att_date", "attendance date"],
  status: ["status", "attendance_status", "att_status"],
  work_hours: ["work_hours", "hours", "work hours"],
  check_in: ["check_in", "time_in", "in", "punch_in"],
  check_out: ["check_out", "time_out", "out", "punch_out"],
  shift: ["shift", "shift_name"],
  remarks: ["remarks", "remark", "notes"],
};

export function normaliseHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
}

function resolveColumnIndex(headers, field) {
  const norms = headers.map((h, i) => ({ raw: h, norm: normaliseHeader(h), index: i }));
  const aliases = new Set((HEADER_ALIASES[field] || [field]).map(normaliseHeader));
  const hit = norms.find((h) => aliases.has(h.norm));
  return hit ? hit.index : -1;
}

function cellString(v) {
  if (v === null || v === undefined) return "";
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return formatIsoDate(v);
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    return Number.isInteger(v) ? String(v) : String(v);
  }
  return String(v).trim();
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatIsoDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Excel serial (1900 date system) → JS Date */
function excelSerialToDate(serial) {
  const utcDays = Math.floor(serial - 25569);
  return new Date(utcDays * 86400 * 1000);
}

function parseTimeStr(val) {
  if (val === null || val === undefined || val === "") return null;
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    return `${pad2(val.getHours())}:${pad2(val.getMinutes())}:${pad2(val.getSeconds())}`;
  }
  if (typeof val === "object" && typeof val.getHours === "function") {
    return `${pad2(val.getHours())}:${pad2(val.getMinutes())}:${pad2(val.getSeconds())}`;
  }
  const s = String(val).trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m) return `${pad2(m[1])}:${pad2(m[2])}:${pad2(m[3] || "00")}`;
  if (typeof val === "number" && val >= 0 && val < 1) {
    const totalMin = Math.round(val * 24 * 60);
    const h = Math.floor(totalMin / 60);
    const min = totalMin % 60;
    return `${pad2(h)}:${pad2(min)}:00`;
  }
  return null;
}

function computeHoursFromTimes(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  const [h1, m1] = checkIn.split(":").map(Number);
  const [h2, m2] = checkOut.split(":").map(Number);
  const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  return diff > 0 ? +(diff / 60).toFixed(2) : 0;
}

/**
 * Normalize Excel/CSV date cells to YYYY-MM-DD for the bulk API.
 */
export function parseWorkDate(val) {
  if (val === null || val === undefined || val === "") {
    return { ok: false, iso: "", error: "work_date is empty" };
  }

  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    return { ok: true, iso: formatIsoDate(val), error: null };
  }

  if (typeof val === "number" && Number.isFinite(val) && val > 0) {
    const d = excelSerialToDate(val);
    if (!Number.isNaN(d.getTime())) {
      return { ok: true, iso: formatIsoDate(d), error: null };
    }
  }

  const s = String(val).trim();
  if (!s) {
    return { ok: false, iso: "", error: "work_date is empty" };
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return { ok: true, iso: s, error: null };
    }
  }

  const mdy = s.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})$/);
  if (mdy) {
    const a = parseInt(mdy[1], 10);
    const b = parseInt(mdy[2], 10);
    let y = parseInt(mdy[3], 10);
    if (y < 100) y += 2000;
    // Prefer D/M/Y when first part > 12 (e.g. 31/3/2026)
    let day = a;
    let month = b;
    if (a <= 12 && b <= 12) {
      // Ambiguous M/D/Y (US-style, common in Excel US locale)
      month = a;
      day = b;
    } else if (a > 12) {
      day = a;
      month = b;
    }
    const d = new Date(y, month - 1, day);
    if (!Number.isNaN(d.getTime()) && d.getFullYear() === y && d.getMonth() === month - 1) {
      return { ok: true, iso: formatIsoDate(d), error: null };
    }
  }

  const dmy = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10);
    const y = parseInt(dmy[3], 10);
    const d = new Date(y, month - 1, day);
    if (!Number.isNaN(d.getTime())) {
      return { ok: true, iso: formatIsoDate(d), error: null };
    }
  }

  return {
    ok: false,
    iso: "",
    error: `Invalid work_date "${s}" — use YYYY-MM-DD or a full date (e.g. 2026-03-01)`,
  };
}

export async function parseAttendanceSpreadsheet(file) {
  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".xlsx")) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array", cellDates: true });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) {
      return { headers: [], rows: [], sheetName: null };
    }
    const ws = wb.Sheets[sheetName];
    // raw: true keeps Excel dates as serial numbers; parseWorkDate normalizes to YYYY-MM-DD
    const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: true });
    if (!matrix.length) {
      return { headers: [], rows: [], sheetName };
    }
    const headers = (matrix[0] || []).map((h) => cellString(h));
    const rows = [];
    for (let i = 1; i < matrix.length; i++) {
      const line = matrix[i] || [];
      if (line.every((c) => cellString(c) === "")) continue;
      const obj = {};
      headers.forEach((h, col) => {
        if (h) obj[h] = line[col];
      });
      rows.push(obj);
    }
    return { headers, rows, sheetName };
  }

  if (name.endsWith(".csv")) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => String(h || "").trim(),
        complete: (res) =>
          resolve({
            headers: res.meta.fields || [],
            rows: res.data || [],
            sheetName: null,
          }),
        error: (err) => reject(err),
      });
    });
  }

  throw new Error("Only .csv and .xlsx files are supported");
}

/**
 * Build rows ready for bulk API from parsed sheet + org employee map.
 */
export function buildAttendanceBulkRows({
  headers,
  rows,
  codeToId,
  idToName,
  idToCode,
  organisationId,
  normalizeStatus,
  debug = false,
}) {
  const log = debug
    ? (...args) => console.info("[attendance-bulk-parse]", ...args)
    : () => {};

  log("parsed headers", headers);
  log("organisation_id for lookup", organisationId);
  log("employee codes in org map", [...(codeToId?.keys() || [])].slice(0, 50));

  const empCol = resolveColumnIndex(headers, "employee_code");
  if (empCol < 0) {
    return {
      ok: false,
      columnError: "employee_code column missing",
      parsed: [],
      errors: [],
    };
  }

  const dateCol = resolveColumnIndex(headers, "work_date");
  const statusCol = resolveColumnIndex(headers, "status");
  const hoursCol = resolveColumnIndex(headers, "work_hours");
  const checkInCol = resolveColumnIndex(headers, "check_in");
  const checkOutCol = resolveColumnIndex(headers, "check_out");
  const remarksCol = resolveColumnIndex(headers, "remarks");

  const parsed = [];
  const errors = [];

  rows.forEach((raw, i) => {
    const rowNum = i + 2;
    const cols = headers.map((h) => cellString(raw?.[h]));
    log(`row ${rowNum} raw values`, cols);

    const rawEmp = cols[empCol] ?? "";
    const rawDateVal = dateCol >= 0 ? raw?.[headers[dateCol]] : "";
    const dateParsed = parseWorkDate(rawDateVal);
    const work_date = dateParsed.iso;
    const work_date_raw = cellString(rawDateVal);
    const statusRaw = statusCol >= 0 ? cols[statusCol] || "P" : "P";
    const rawCheckIn = checkInCol >= 0 ? raw?.[headers[checkInCol]] : null;
    const rawCheckOut = checkOutCol >= 0 ? raw?.[headers[checkOutCol]] : null;
    const check_in = parseTimeStr(rawCheckIn);
    const check_out = parseTimeStr(rawCheckOut);
    const work_hours = hoursCol >= 0
      ? cols[hoursCol] || "0"
      : String(computeHoursFromTimes(check_in, check_out));
    const remarks = remarksCol >= 0 ? cols[remarksCol] : "";

    log(`row ${rowNum} employee_code extracted`, rawEmp);

    if (!rawEmp) {
      errors.push({ row: rowNum, error: "employee_code is empty", employee_code: "" });
      return;
    }

    let employee_id = "";
    let resolveError = null;

    if (UUID_RE.test(rawEmp)) {
      employee_id = rawEmp;
      log(`row ${rowNum} lookup`, { type: "uuid", employee_id });
    } else {
      const key = rawEmp.toLowerCase();
      employee_id = codeToId.get(key) || "";
      log(`row ${rowNum} lookup`, {
        type: "code",
        key,
        found: Boolean(employee_id),
        employee_id: employee_id || null,
      });
      if (!employee_id) {
        resolveError = `Unknown employee_code: ${rawEmp} (not in your organisation)`;
      }
    }

    if (resolveError) {
      errors.push({ row: rowNum, error: resolveError, employee_code: rawEmp });
      return;
    }
    if (!dateParsed.ok) {
      errors.push({
        row: rowNum,
        error: dateParsed.error || "Invalid work_date",
        employee_code: rawEmp,
        work_date_raw,
      });
      return;
    }

    const time_in = check_in && work_date ? `${work_date}T${check_in}` : null;
    const time_out = check_out && work_date ? `${work_date}T${check_out}` : null;

    parsed.push({
      row: rowNum,
      employee_code: UUID_RE.test(rawEmp) ? "" : rawEmp,
      employee_name: employeeNameForId(idToName, idToCode, employee_id),
      employee_id,
      work_date,
      work_date_raw: work_date_raw !== work_date ? work_date_raw : "",
      status: normalizeStatus ? normalizeStatus(statusRaw) : statusRaw,
      work_hours: Number(work_hours || 0),
      check_in: check_in || null,
      check_out: check_out || null,
      time_in,
      time_out,
      remarks: remarks || null,
      preview_status: statusRaw,
    });
  });

  return { ok: true, columnError: null, parsed, errors };
}

export function buildCodeToIdMap(employees, organisationId) {
  const m = new Map();
  for (const e of employees || []) {
    if (organisationId && e.organisation_id && String(e.organisation_id) !== String(organisationId)) {
      continue;
    }
    const code = cellString(e.employee_code);
    if (!code) continue;
    m.set(code.toLowerCase(), e.employee_id);
  }
  return m;
}

export function buildEmployeeLookupMaps(employees, organisationId) {
  const codeToId = buildCodeToIdMap(employees, organisationId);
  const idToName = new Map();
  const idToCode = new Map();
  for (const e of employees || []) {
    if (organisationId && e.organisation_id && String(e.organisation_id) !== String(organisationId)) {
      continue;
    }
    const id = e.employee_id;
    if (!id) continue;
    const name =
      String(e.display_name || "").trim() ||
      [e.first_name, e.last_name].filter(Boolean).join(" ").trim() ||
      "";
    idToName.set(String(id), name);
    if (e.employee_code) idToCode.set(String(id), e.employee_code);
  }
  return { codeToId, idToName, idToCode };
}

function employeeNameForId(idToName, idToCode, employeeId) {
  if (!employeeId) return "";
  const name = idToName.get(String(employeeId)) || "";
  const code = idToCode.get(String(employeeId)) || "";
  if (name && code) return `${name} (${code})`;
  return name || code || "";
}
